<template>
  <v-layout class="h-screen height-screen">
    <DeepSupportPanel
      v-model="supportPanel"
      :class="{ 'd-none': !supportPanel }"
    />

    <OrganizationDataProvider
      v-if="currentDocument"
      :company-id="currentDocument.companyId"
      :document-id="currentDocument.documentId"
    >
      <DocumentDrawer
        v-if="currentDocument && showDocument"
        v-model:support-panel="supportPanel"
      />
    </OrganizationDataProvider>

    <DocumentToolbar
      v-if="showDocument && currentDocument"
      :document="currentDocument"
    />

    <v-main>
      <v-container class="container-wrapper" fluid>
        <router-view />
      </v-container>
      <SignatureRejectDialog />
      <GuestSendEmailDialog />
      <DeleteDocumentsDialog />
      <DocumentWithdrawDialog />
    </v-main>
  </v-layout>
</template>

<script setup lang="ts">
import SignatureRejectDialog from '@/components/dialogs/SignatureRejectDialog.vue';
import GuestSendEmailDialog from '@/components/dialogs/GuestSendEmailDialog.vue';
import DocumentWithdrawDialog from '@/components/dialogs/DocumentWithdrawDialog.vue';
import DocumentToolbar from '@/components/documents/DocumentToolbar.vue';
import DocumentDrawer from '@/components/drawers/DocumentDrawer.vue';
import DeleteDocumentsDialog from '@/components/dialogs/DeleteDocumentsDialog.vue';
import { useSignStore } from '@/stores/sign.ts';
import { computed, onMounted, onUnmounted, provide, ref } from 'vue';
import { useMachine } from '@xstate/vue';
import { inviteMachine } from '@/machines/inviteMachine';
import AuthService from '@/services/authService.ts';
import { useRoute } from 'vue-router';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { useSignContext } from '@/composables/useSignContext';
import { useDeepAdminStore } from '@/stores/deepadmin/deepadmin';
import { useCreateStore } from '@/stores/create.ts';
import DeepSupportPanel from '@/components/DeepSupportPanel.vue';
import OrganizationDataProvider from '@/components/OrganizationDataProvider.vue';
import { useTheme } from 'vuetify';

import { usePlaceholders } from '@/composables/placement/usePlacement.ts';
import {
  PlaceHolderComposableKey,
  PlaceHolderSealComposableKey
} from '@/composables/placement/placementSymbols.ts';
import { useSealAnnotation } from '@/composables/placement/useSealAnnotation.ts';
import { SignStatus } from '@/types/enums/SignStatus.ts';
import { DocumentStatus } from '@/types/enums/DocumentStatus.ts';
import { useRedirectAfterSign } from '@/composables/useRedirectAfterSign.ts';
import { storeToRefs } from 'pinia';

const deepAdminStore = useDeepAdminStore();
const createStore = useCreateStore();
const signStore = useSignStore();
const defaultPrimaryColor = ref();
const defaultDarkPrimaryColor = ref();
const theme = useTheme();

if (AuthService.isAuthenticated) {
  deepAdminStore.fetchUserInfo();
}

onMounted(() => {
  defaultPrimaryColor.value = theme.global.current.value.colors.primary;
  defaultDarkPrimaryColor.value = theme.themes.value.dark.colors.primary;
});

onUnmounted(() => {
  createStore.document = undefined;
  signStore.document = null;
  theme.themes.value.light.colors.primary = defaultPrimaryColor.value;
  theme.themes.value.dark.colors.primary = defaultDarkPrimaryColor.value;
});

const { signKey } = useSignContext();
const { currentDocument } = useDocumentContext();
const route = useRoute();

const supportPanel = ref(false);

const isInvite = computed(() => {
  return route.name === 'sign-id';
});

const { snapshot: inviteState } = useMachine(inviteMachine, {
  input: {
    isInvite: isInvite.value,
    invitationLink: window.location.href,
    user: AuthService.user,
    info: signStore.signKeyInfo,
    signKey: signKey.value,
    signStore
  }
});

const showDocument = computed(() =>
  inviteState.value.matches({
    document: { ready: { access: 'allowed' } }
  })
);

const {
  onSignatureDragOver,
  onSignatureDrop,
  currentPlaceholders,
  resetPlaceholderState,
  resetPlaceholders,
  placePlaceholdersByAutographData,
  placeholderTemplateRefs,
  placePlaceholdersAutomatically,
  createOrReuseSignatureSlot,
  movePlaceHolders,
  placeholderSetupComplete,
  addPlaceholderFromDrop,
  placeholderRemountNonces,
  requestPlaceholderRemount,
  isEnvelopeMode
} = usePlaceholders();

provide(PlaceHolderComposableKey, {
  onSignatureDragOver,
  onSignatureDrop,
  addPlaceholderFromDrop,
  currentPlaceholders,
  placeholderTemplateRefs,
  resetPlaceholderState,
  resetPlaceholders,
  placePlaceholdersByAutographData,
  placePlaceholdersAutomatically,
  createOrReuseSignatureSlot,
  movePlaceHolders,
  placeholderSetupComplete,
  placeholderRemountNonces,
  requestPlaceholderRemount,
  isEnvelopeMode
});

const {
  sealAnnotationPlaceholder,
  sealPlaceholder,
  handleInitialSealAnnotationPosition,
  handleInitialSealPosition
} = useSealAnnotation(currentPlaceholders, placeholderTemplateRefs);

provide(PlaceHolderSealComposableKey, {
  sealAnnotationPlaceholder,
  sealPlaceholder,
  handleInitialSealAnnotationPosition,
  handleInitialSealPosition
});
const { signKeyInfo } = storeToRefs(signStore);

const documentId = computed<string | undefined>(() => {
  const _documentId = Array.isArray(route.params.document)
    ? route.params.document[0]
    : route.params.document;
  return _documentId ? _documentId : signKeyInfo.value?.documentId;
});

const redirectAfterSignStatus = computed<SignStatus>(() => {
  const isDocumentRejected =
    currentDocument.value?.documentStatus === DocumentStatus.WITHDRAWN ||
    currentDocument.value?.documentStatus === DocumentStatus.REJECTED;
  const isDocumentSigned =
    currentDocument.value?.documentStatus === DocumentStatus.SIGNED;
  if (
    isDocumentSigned ||
    currentDocument.value?.signContext?.signStatus === SignStatus.SIGNED
  ) {
    return SignStatus.SIGNED;
  }

  if (
    isDocumentRejected ||
    currentDocument.value?.signContext?.signStatus === SignStatus.REJECTED
  ) {
    return SignStatus.REJECTED;
  }

  return SignStatus.PENDING;
});

useRedirectAfterSign({
  documentId,
  redirectUrlQueryValue: computed(() => route.query['redirect-url']),
  signStatus: redirectAfterSignStatus
});
</script>

<style lang="scss">
.container-wrapper {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
}

.v-layout {
  z-index: auto !important;
}

.v-main {
  padding-bottom: 0 !important;
}
</style>
